import { atom, RecoilState, selector, selectorFamily } from 'recoil';
import localforage from 'localforage';
import localForageEffect from './effects/localForageEffect';
import setSelectedVehiclesToSocketServerEffect from './effects/setSelectedVehiclesToSocketServerEffect';
import {
  IVehicleOnlinePanels,
  onlinePanelAtom,
} from '../../components/OnlinePanel/OnlinePanelState';
import {
  boundsAtom,
  isClusterEnabledAtom,
  zoomAtom,
} from '../../components/OnlineMapNew/OnlineMapState';
import { MIN_POINTS_FOR_DISABLED_CLUSTER } from '../../components/OnlineMapNew/GoogleMapLayer/constants';
import Config from '../../../config';
import { getUserId } from '../../Services/UserService';
import { IOption } from '../../components/Forms/ReactHookFormFields/Calendar';
import {
  ILastRideDistanceCumulative,
  IVehicle,
  IVehicleGdprDataAllowed,
  IVehicleGroup,
  IVehicles,
  IVehiclesWithData,
  IVehicleWithData,
  VehiclesBasicInfo,
} from '../../components/Vehicles/types';
import { SuperclusterWorker } from '../../components/OnlineMapNew/CreateWorkers';
import { selectedCustomersAtom } from './customers';
import { vehiclesDataAtom } from './vehiclesData';

export const getSelectedVehiclesFromIndexedDb = async (): Promise<number[]> => {
  const selectedVehicles = await localforage.getItem<number[]>('selectedVehicles');
  if (!selectedVehicles) {
    return [];
  }
  return selectedVehicles;
};

// State for storing info about vehicles
// This state is upated from socket events, so it changes frequently.s
// It should be used only when we need to get realtime data about vehicles
export const vehiclesAtom = atom<IVehicles>({
  key: 'vehicles',
  default: {},
});

// Used for options in the vehicle select
export const vehiclesOptionsAtom = atom<IOption[]>({
  key: 'vehiclesOptions',
  default: [],
});

/**
 * State for storing basic info about vehicles (id, name, licenceNumber, vehicleTypeId, customerId)
 * This state is updated only when vehicles are loaded. So it can be used everywhere
 * when we need to get basic info about vehicles wihtout triggering changes
 */
export const vehiclesBasicInfoAtom = atom<VehiclesBasicInfo>({
  key: 'vehiclesBasicInfo',
  default: {},
});

export const vehicleGroupsAtom = atom<IVehicleGroup[]>({
  key: 'vehicleGroups',
  default: [],
});

export const selectedVehiclesAtom = atom<number[]>({
  key: 'selectedVehicles',
  // default: getSelectedVehiclesFromIndexedDb(),
  default: [],
  effects: [localForageEffect('selectedVehicles'), setSelectedVehiclesToSocketServerEffect()],
});

export const selectedVehiclesWithBasicInfoSelector = selector<VehiclesBasicInfo>({
  key: 'selectedVehiclesWithBasicInfo',
  get: ({ get }) => {
    const selectedVehiclesList = get(selectedVehiclesAtom);
    const vehiclesList = get(vehiclesBasicInfoAtom);
    const selectedVehiclesWithBasicInfo: VehiclesBasicInfo = {};
    selectedVehiclesList.forEach((vehicleId: number) => {
      const vehicle = vehiclesList[vehicleId];
      if (vehicle) {
        selectedVehiclesWithBasicInfo[vehicleId] = vehicle;
      }
    });
    return selectedVehiclesWithBasicInfo;
  },
});

export const vehicleGroupsBySelectedCustomersSelector = selector<IVehicleGroup[]>({
  key: 'vehicleGroupsBySelectedCustomers',
  get: ({ get }) => {
    const selectedCustomers = get(selectedCustomersAtom);
    const vehicleGroupsList = get(vehicleGroupsAtom);
    return vehicleGroupsList.filter((vehicleGroup: IVehicleGroup) =>
      selectedCustomers.includes(vehicleGroup.customerId)
    );
  },
});

export const isVehicleGdprDataAllowedAtom = atom<IVehicleGdprDataAllowed>({
  key: 'isVehicleGdprDataAllowed',
  default: {},
});

export const selectedVehiclesWithDataSelector = selector<IVehiclesWithData>({
  key: 'selectedVehiclesWithData',
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
  get: ({ get }) => {
    const selectedVehiclesList = get(selectedVehiclesAtom);
    const isVehicleGdprDataAllowedData = get(isVehicleGdprDataAllowedAtom);
    const vehiclesList = get(vehiclesAtom);
    const vehiclesDataList = get(vehiclesDataAtom);
    const isClusterEnabledValue = get<boolean>(isClusterEnabledAtom);
    const onlinePanels = get<IVehicleOnlinePanels>(onlinePanelAtom);
    const vehiclesWithDataList: IVehiclesWithData = {};
    const zoomData = get(zoomAtom);
    const boundsData = get(boundsAtom);
    selectedVehiclesList.forEach((vehicleId: number) => {
      const vehicle = vehiclesList[vehicleId];
      const vehicleData = vehiclesDataList[vehicleId];
      const onlinePanel = onlinePanels[vehicleId];

      if (
        vehicle &&
        vehicleData &&
        vehicleData.position &&
        vehicleData.position.latitude &&
        vehicleData.position.longitude
      ) {
        const newVehicleWithData: IVehicleWithData = {
          id: vehicle.id,
          name: vehicle.name,
          licenceNumber: vehicle.licenceNumber,
          vehicleTypeId: vehicle.vehicleTypeId,
          vehicleGroups: vehicle.vehicleGroups,
          customerId: vehicle.customerId,
          driver: vehicle.driver,
          showInNavigator: vehicle.showInNavigator,
          showInNavigatorGroups: vehicle.showInNavigatorGroups,
          status: vehicleData.status,
          position: vehicleData.position,
          // isPrivate: vehicleData.isPrivate,
          drivingMode: vehicleData.drivingMode,
          lastRide: vehicleData.lastRide,
          onlinePanel,
        };
        vehiclesWithDataList[vehicleId] = newVehicleWithData;
      }
    });
    const userId = getUserId();
    if (userId) {
      SuperclusterWorker.postMessage({
        vehiclesData: vehiclesWithDataList,
        bounds: boundsData,
        zoom: zoomData,
        minPoints: isClusterEnabledValue
          ? Config.onlineMap.superCluster.minPoints
          : MIN_POINTS_FOR_DISABLED_CLUSTER,
        isVehicleGdprDataAllowed: isVehicleGdprDataAllowedData,
        userId,
      });
    }
    return vehiclesWithDataList;
  },
});

// Vehicles/VehiclesState.vehicle()
export const vehicleWithDataSelector = selectorFamily<IVehicleWithData | null, number | false>({
  key: 'vehicleWithData',
  get:
    (vehicleId: number | false) =>
    ({ get }) => {
      if (!vehicleId) {
        return null;
      }
      const vehiclesList = get(vehiclesAtom);
      const vehiclesDataList = get(vehiclesDataAtom);
      const onlinePanels = get<IVehicleOnlinePanels>(onlinePanelAtom);
      const vehicle = vehiclesList[vehicleId];
      const vehicleData = vehiclesDataList[vehicleId];
      const onlinePanel = onlinePanels[vehicleId];

      if (!vehicle) {
        console.warn(`vehicleWithData: vehicle ${vehicleId} not found.`);
        return null;
      }

      const withData: IVehicleWithData = {
        id: vehicle.id,
        name: vehicle.name,
        licenceNumber: vehicle.licenceNumber,
        vehicleTypeId: vehicle.vehicleTypeId,
        vehicleGroups: vehicle.vehicleGroups,
        customerId: vehicle.customerId,
        driver: vehicle.driver,
        showInNavigator: vehicle.showInNavigator,
        showInNavigatorGroups: vehicle.showInNavigatorGroups,
        status: vehicleData.status,
        position: vehicleData.position,
        // isPrivate: vehicleData.isPrivate,
        drivingMode: vehicleData.drivingMode,
        lastRide: vehicleData.lastRide,
        onlinePanel,
      };
      return withData;
    },
});

export const vehicleByIdSelector = selectorFamily<IVehicle | null, number | false>({
  key: 'vehicleById',
  get:
    (vehicleId: number | false) =>
    ({ get }) => {
      if (!vehicleId) {
        return null;
      }
      const vehiclesList = get(vehiclesAtom);
      return vehiclesList[vehicleId];
    },
});

export const vehicleAtom = (id: number): RecoilState<IVehicle | null> =>
  atom<IVehicle | null>({
    key: `vehicle_${id}`,
    default: null,
  });

export const selectedVehicleStatusAtom = atom<string>({
  key: 'status',
  default: '',
});

export const lastRideDistanceCumulativeAtom = atom<ILastRideDistanceCumulative | null>({
  key: 'lastRideDistanceCumulative',
  default: null,
});
